import { Suspense, lazy } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { ClerkProvider } from '@clerk/clerk-react';
import { ROUTE_NAMES } from '@tsgecart/config';
import { queryClient } from './lib/query-client.js';
import { webEnv } from './lib/env.js';
import { AppShell } from './components/app-shell.js';
import { RequireAuth, RequireAdmin } from './lib/auth/route-guards.js';
import { HomePage } from './routes/home-page.js';
import { AboutPage } from './routes/about-page.js';
import { ContactPage } from './routes/contact-page.js';
import { NotFoundPage } from './routes/not-found-page.js';
import { ForbiddenPage } from './routes/forbidden-page.js';
import { ClerkNotConfiguredPage } from './routes/clerk-not-configured-page.js';
import { SignInPage } from './routes/sign-in-page.js';
import { SignUpPage } from './routes/sign-up-page.js';
import { AdminLayout } from './features/admin/layouts/admin-layout.js';
import { DashboardPage } from './features/admin/pages/dashboard-page.js';
import { ProductsListPage } from './features/admin/catalog/pages/products-list-page.js';
import { ProductEditorPage } from './features/admin/catalog/pages/product-editor-page.js';
import { CategoriesPage } from './features/admin/catalog/pages/categories-page.js';
import { BrandsPage } from './features/admin/catalog/pages/brands-page.js';
import { InventoryPage } from './features/admin/inventory/pages/inventory-page.js';
import { InventoryMovementsPage } from './features/admin/inventory/pages/inventory-movements-page.js';
import { WarehousesPage } from './features/admin/inventory/pages/warehouses-page.js';
import { MediaLibraryPage } from './features/admin/media/pages/media-library-page.js';
import { OrdersListPage } from './features/admin/orders/pages/orders-list-page.js';
import { OrderDetailPage } from './features/admin/orders/pages/order-detail-page.js';
import { PaymentsListPage } from './features/admin/payments/pages/payments-list-page.js';
import { PaymentDetailPage } from './features/admin/payments/pages/payment-detail-page.js';
import { RefundsListPage } from './features/admin/payments/pages/refunds-list-page.js';
import { CustomersDirectoryPage } from './features/admin/customers/pages/customers-directory-page.js';
import { CustomerProfilePage } from './features/admin/customers/pages/customer-profile-page.js';
import { AdminLoadingState } from './features/admin/components/primitives/admin-loading-state.js';
import { StorefrontLoadingState } from './features/storefront/components/states.js';
import { ShopPage } from './features/storefront/catalog/pages/shop-page.js';
import { CategoryPage } from './features/storefront/catalog/pages/category-page.js';
import { ProductDetailPage } from './features/storefront/catalog/pages/product-detail-page.js';
import { SearchPage } from './features/storefront/catalog/pages/search-page.js';
import { CartPage } from './features/storefront/cart/pages/cart-page.js';
import { WishlistPage } from './features/storefront/wishlist/pages/wishlist-page.js';
import { EarnWithFunPage } from './features/storefront/rewards/pages/earn-with-fun-page.js';
import { AccountLayout } from './features/storefront/account/components/account-layout.js';

// Customer Storefront milestone -- checkout/order-success and the whole
// authenticated account area are lazy-loaded (same rationale as Admin
// Dashboard Milestone 4's lazy admin pages: these are visited less often
// than the core browse/product/cart path, which stays eager for a
// snappier first-load shopping experience with no Suspense flicker on
// the hot path).
const CheckoutPage = lazy(() =>
  import('./features/storefront/checkout/pages/checkout-page.js').then((m) => ({ default: m.CheckoutPage })),
);
const OrderSuccessPage = lazy(() =>
  import('./features/storefront/checkout/pages/order-success-page.js').then((m) => ({ default: m.OrderSuccessPage })),
);
const TrackOrderPage = lazy(() =>
  import('./features/storefront/orders/pages/track-order-page.js').then((m) => ({ default: m.TrackOrderPage })),
);
const AccountProfilePage = lazy(() =>
  import('./features/storefront/account/pages/profile-page.js').then((m) => ({ default: m.AccountProfilePage })),
);
const AccountOrdersPage = lazy(() =>
  import('./features/storefront/orders/pages/orders-list-page.js').then((m) => ({ default: m.AccountOrdersPage })),
);
const AccountOrderDetailPage = lazy(() =>
  import('./features/storefront/orders/pages/order-detail-page.js').then((m) => ({ default: m.AccountOrderDetailPage })),
);
const AccountAddressesPage = lazy(() =>
  import('./features/storefront/addresses/pages/addresses-page.js').then((m) => ({ default: m.AccountAddressesPage })),
);
const AccountRewardsPage = lazy(() =>
  import('./features/storefront/rewards/pages/account-rewards-page.js').then((m) => ({ default: m.AccountRewardsPage })),
);
const AccountNotificationsPage = lazy(() =>
  import('./features/storefront/notifications/pages/account-notifications-page.js').then((m) => ({
    default: m.AccountNotificationsPage,
  })),
);

// Admin Dashboard Milestone 4 -- lazy-loaded to keep these newer, less-
// frequently-visited admin areas out of the main bundle (the eager-import
// convention every earlier milestone's admin page used had grown the main
// chunk past Vite's 500kB warning threshold by the time this milestone
// added its 8th feature area). Milestones 1-3 pages above are left as they
// were -- this is a scoped, additive performance fix, not a repo-wide
// lazy-loading migration.
const CouponsListPage = lazy(() =>
  import('./features/admin/marketing/coupons/pages/coupons-list-page.js').then((m) => ({ default: m.CouponsListPage })),
);
const CouponEditorPage = lazy(() =>
  import('./features/admin/marketing/coupons/pages/coupon-editor-page.js').then((m) => ({ default: m.CouponEditorPage })),
);
const RewardsListPage = lazy(() =>
  import('./features/admin/marketing/rewards/pages/rewards-list-page.js').then((m) => ({ default: m.RewardsListPage })),
);
const RewardConfigDetailPage = lazy(() =>
  import('./features/admin/marketing/rewards/pages/reward-config-detail-page.js').then((m) => ({
    default: m.RewardConfigDetailPage,
  })),
);
const BannersListPage = lazy(() =>
  import('./features/admin/marketing/banners/pages/banners-list-page.js').then((m) => ({ default: m.BannersListPage })),
);
const BannerEditorPage = lazy(() =>
  import('./features/admin/marketing/banners/pages/banner-editor-page.js').then((m) => ({ default: m.BannerEditorPage })),
);
const HomepageBannersListPage = lazy(() =>
  import('./features/admin/content/banners/pages/homepage-banners-list-page.js').then((m) => ({
    default: m.HomepageBannersListPage,
  })),
);
const HomepageBannerEditorPage = lazy(() =>
  import('./features/admin/content/banners/pages/homepage-banner-editor-page.js').then((m) => ({
    default: m.HomepageBannerEditorPage,
  })),
);
const NotificationsListPage = lazy(() =>
  import('./features/admin/notifications/pages/notifications-list-page.js').then((m) => ({
    default: m.NotificationsListPage,
  })),
);
const NotificationDeliveriesPage = lazy(() =>
  import('./features/admin/notifications/pages/notification-deliveries-page.js').then((m) => ({
    default: m.NotificationDeliveriesPage,
  })),
);
const SettingsPage = lazy(() =>
  import('./features/admin/settings/pages/settings-page.js').then((m) => ({ default: m.SettingsPage })),
);
const AuditLogsPage = lazy(() =>
  import('./features/admin/audit/pages/audit-logs-page.js').then((m) => ({ default: m.AuditLogsPage })),
);
const ReportsHubPage = lazy(() =>
  import('./features/admin/reports/pages/reports-hub-page.js').then((m) => ({ default: m.ReportsHubPage })),
);
const SystemHealthPage = lazy(() =>
  import('./features/admin/system/pages/system-health-page.js').then((m) => ({ default: m.SystemHealthPage })),
);
const JobsPage = lazy(() =>
  import('./features/admin/system/pages/jobs-page.js').then((m) => ({ default: m.JobsPage })),
);
const WebhooksPage = lazy(() =>
  import('./features/admin/system/pages/webhooks-page.js').then((m) => ({ default: m.WebhooksPage })),
);
const RbacPage = lazy(() =>
  import('./features/admin/system/pages/rbac-page.js').then((m) => ({ default: m.RbacPage })),
);

const CLERK_PUBLISHABLE_KEY = webEnv.VITE_CLERK_PUBLISHABLE_KEY;

export function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <ClerkAppShell />
        </BrowserRouter>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

/** The storefront chrome (header/sign-in link) -- never rendered around `/admin/*`, which has its own AdminLayout shell (sidebar/topbar). Kept as a layout route (`<Outlet />`) so it composes with react-router's path ranking exactly like every other route group, rather than a manual location check. */
function StorefrontLayout() {
  return (
    <AppShell>
      <Suspense fallback={<StorefrontLoadingState label="Loading page" />}>
        <Outlet />
      </Suspense>
    </AppShell>
  );
}

/** `/admin/*` requires sign-in AND the ADMIN/STAFF role before AdminLayout ever renders -- see docs/admin-dashboard-architecture.md "Access behavior". */
function AdminSection() {
  return (
    <RequireAuth>
      <RequireAdmin>
        <AdminLayout>
          <Suspense fallback={<AdminLoadingState label="Loading page" />}>
            <Outlet />
          </Suspense>
        </AdminLayout>
      </RequireAdmin>
    </RequireAuth>
  );
}

/**
 * Split out from App() so BrowserRouter is preserved outside ClerkProvider
 * (existing provider ordering) while still being available to
 * react-router-aware children. This @clerk/clerk-react version has no
 * `navigate` prop to wire to react-router's history (removed/changed
 * across versions) -- Clerk's own components use the browser History API
 * directly for their internal redirects, which composes fine with
 * react-router without an explicit adapter.
 */
function ClerkAppShell() {
  if (!CLERK_PUBLISHABLE_KEY) {
    return <ClerkNotConfiguredPage />;
  }

  return (
    <ClerkProvider publishableKey={CLERK_PUBLISHABLE_KEY}>
      <Routes>
        <Route element={<StorefrontLayout />}>
          <Route path={ROUTE_NAMES.home} element={<HomePage />} />
          <Route path={`${ROUTE_NAMES.signIn}/*`} element={<SignInPage />} />
          <Route path={`${ROUTE_NAMES.signUp}/*`} element={<SignUpPage />} />

          {/* Marketing pages -- public, eager (simple, low-priority-but-
              frequently-linked pages, not big data-heavy pages). */}
          <Route path={ROUTE_NAMES.about} element={<AboutPage />} />
          <Route path={ROUTE_NAMES.contact} element={<ContactPage />} />

          {/* Legacy Phase-0 routes -- real redirect sources, never a 404 for an old bookmark/link. */}
          <Route path={ROUTE_NAMES.profile} element={<Navigate to={ROUTE_NAMES.accountProfile} replace />} />
          <Route path={ROUTE_NAMES.orders} element={<Navigate to={ROUTE_NAMES.accountOrders} replace />} />

          {/* Catalog browsing -- public, eager (core shopping path). */}
          <Route path={ROUTE_NAMES.shop} element={<ShopPage />} />
          <Route path={`${ROUTE_NAMES.category}/:slug`} element={<CategoryPage />} />
          <Route path={`${ROUTE_NAMES.product}/:slug`} element={<ProductDetailPage />} />
          <Route path={ROUTE_NAMES.search} element={<SearchPage />} />
          <Route path={ROUTE_NAMES.earnWithFun} element={<EarnWithFunPage />} />

          {/* Cart -- authenticated, eager (core shopping path). */}
          <Route
            path={ROUTE_NAMES.cart}
            element={
              <RequireAuth>
                <CartPage />
              </RequireAuth>
            }
          />

          {/* Checkout / order-success / track-order -- authenticated, lazy. */}
          <Route
            path={ROUTE_NAMES.checkout}
            element={
              <RequireAuth>
                <CheckoutPage />
              </RequireAuth>
            }
          />
          <Route
            path={`${ROUTE_NAMES.orderSuccess}/:orderNumber`}
            element={
              <RequireAuth>
                <OrderSuccessPage />
              </RequireAuth>
            }
          />
          <Route
            path={ROUTE_NAMES.trackOrder}
            element={
              <RequireAuth>
                <TrackOrderPage />
              </RequireAuth>
            }
          />

          {/* Account area -- `account` itself has no page, redirects to `accountProfile` (same "no page of its own" pattern as `admin` -> `adminDashboard`). Every nested page composes with the shared `AccountLayout`. */}
          <Route path={ROUTE_NAMES.account} element={<Navigate to={ROUTE_NAMES.accountProfile} replace />} />
          <Route
            path={ROUTE_NAMES.accountProfile}
            element={
              <RequireAuth>
                <AccountLayout>
                  <AccountProfilePage />
                </AccountLayout>
              </RequireAuth>
            }
          />
          <Route
            path={ROUTE_NAMES.accountOrders}
            element={
              <RequireAuth>
                <AccountLayout>
                  <AccountOrdersPage />
                </AccountLayout>
              </RequireAuth>
            }
          />
          <Route
            path={`${ROUTE_NAMES.accountOrders}/:orderNumber`}
            element={
              <RequireAuth>
                <AccountLayout>
                  <AccountOrderDetailPage />
                </AccountLayout>
              </RequireAuth>
            }
          />
          <Route
            path={ROUTE_NAMES.accountAddresses}
            element={
              <RequireAuth>
                <AccountLayout>
                  <AccountAddressesPage />
                </AccountLayout>
              </RequireAuth>
            }
          />
          <Route
            path={ROUTE_NAMES.accountWishlist}
            element={
              <RequireAuth>
                <AccountLayout>
                  <WishlistPage />
                </AccountLayout>
              </RequireAuth>
            }
          />
          <Route
            path={ROUTE_NAMES.accountRewards}
            element={
              <RequireAuth>
                <AccountLayout>
                  <AccountRewardsPage />
                </AccountLayout>
              </RequireAuth>
            }
          />
          <Route
            path={ROUTE_NAMES.accountNotifications}
            element={
              <RequireAuth>
                <AccountLayout>
                  <AccountNotificationsPage />
                </AccountLayout>
              </RequireAuth>
            }
          />

          <Route path={ROUTE_NAMES.forbidden} element={<ForbiddenPage />} />
          <Route path={ROUTE_NAMES.notFound} element={<NotFoundPage />} />
        </Route>

        <Route path={ROUTE_NAMES.admin} element={<AdminSection />}>
          <Route index element={<Navigate to={ROUTE_NAMES.adminDashboard} replace />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="products" element={<ProductsListPage />} />
          <Route path="products/new" element={<ProductEditorPage />} />
          <Route path="products/:productId" element={<ProductEditorPage />} />
          <Route path="categories" element={<CategoriesPage />} />
          <Route path="brands" element={<BrandsPage />} />
          <Route path="inventory" element={<InventoryPage />} />
          <Route path="inventory/movements" element={<InventoryMovementsPage />} />
          <Route path="warehouses" element={<WarehousesPage />} />
          <Route path="media" element={<MediaLibraryPage />} />
          <Route path="orders" element={<OrdersListPage />} />
          <Route path="orders/:orderNumber" element={<OrderDetailPage />} />
          <Route path="payments" element={<PaymentsListPage />} />
          <Route path="payments/:paymentId" element={<PaymentDetailPage />} />
          <Route path="refunds" element={<RefundsListPage />} />
          <Route path="customers" element={<CustomersDirectoryPage />} />
          <Route path="customers/:customerId" element={<CustomerProfilePage />} />
          <Route path="marketing/coupons" element={<CouponsListPage />} />
          <Route path="marketing/coupons/new" element={<CouponEditorPage />} />
          <Route path="marketing/coupons/:couponId" element={<CouponEditorPage />} />
          <Route path="marketing/rewards" element={<RewardsListPage />} />
          <Route path="marketing/rewards/:rewardConfigId" element={<RewardConfigDetailPage />} />
          <Route path="marketing/banners" element={<BannersListPage />} />
          <Route path="marketing/banners/new" element={<BannerEditorPage />} />
          <Route path="marketing/banners/:bannerId" element={<BannerEditorPage />} />
          <Route path="content/banners" element={<HomepageBannersListPage />} />
          <Route path="content/banners/new" element={<HomepageBannerEditorPage />} />
          <Route path="content/banners/:bannerId" element={<HomepageBannerEditorPage />} />
          <Route path="notifications" element={<NotificationsListPage />} />
          <Route path="notifications/deliveries" element={<NotificationDeliveriesPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="audit-logs" element={<AuditLogsPage />} />
          <Route path="reports" element={<ReportsHubPage />} />
          <Route path="system" element={<SystemHealthPage />} />
          <Route path="system/jobs" element={<JobsPage />} />
          <Route path="system/webhooks" element={<WebhooksPage />} />
          <Route path="system/rbac" element={<RbacPage />} />
        </Route>
      </Routes>
    </ClerkProvider>
  );
}
